import { oc } from "@orpc/contract";
import { PRINT_PAGE_SIZE } from "@packages/core/contract/print.ts";
import { z } from "zod";
import { NineBoxResultSchema } from "@domains/reviews/shared/nineBox.ts";
import { MemberFiltersSchema } from "@domains/reviews/shared/reviewee-filters/memberFilters.ts";
import { ScoreBandSchema } from "@domains/reviews/shared/scoreBands.ts";
import { DEFAULT_SCORE_BUCKET_GRANULARITY, ScoreBucketGranularitySchema } from "@domains/reviews/shared/scoreBuckets.ts";
import { ScoreDistributionSchema } from "@domains/reviews/shared/scoreDistribution.ts";
import { PeriodSelectionSchema } from "#shared/periodSelection.ts";
export type { ScoreBand } from "@domains/reviews/shared/scoreBands.ts";

const ScoreSelectionInputSchema = z.object({
    ...PeriodSelectionSchema.shape,
    ...MemberFiltersSchema.shape
});

const ScoreDistributionInputSchema = z.object({
    ...ScoreSelectionInputSchema.shape,
    granularity: ScoreBucketGranularitySchema.default(DEFAULT_SCORE_BUCKET_GRANULARITY)
});

const ReviewScoreDistributionResultSchema = z.object({
    granularity: ScoreBucketGranularitySchema,
    performance: ScoreDistributionSchema,
    potential: ScoreDistributionSchema,
    overall: ScoreDistributionSchema
});
export type ReviewScoreDistributionResult = z.infer<typeof ReviewScoreDistributionResultSchema>;

export { NineBoxCellIds, type NineBoxCell, type NineBoxCellId } from "@domains/reviews/shared/nineBox.ts";
export type { NineBoxResult as ReviewScoreNineBoxResult } from "@domains/reviews/shared/nineBox.ts";

const CountSchema = z.number().int().nonnegative();

const ReviewerTypes = ["self", "manager", "peer", "upward"] as const;
const ReviewerTypeSchema = z.enum(ReviewerTypes);
export type ReviewerType = z.infer<typeof ReviewerTypeSchema>;

const ReviewerTypeAverageSchema = z.object({
    reviewerType: ReviewerTypeSchema,
    average: z.number()
});
export type ReviewerTypeAverage = z.infer<typeof ReviewerTypeAverageSchema>;

const ListReviewerTypeAveragesResultSchema = z.object({
    items: z.array(ReviewerTypeAverageSchema),
    scale: z.number().int().positive()
});
export type ListReviewerTypeAveragesResult = z.infer<typeof ListReviewerTypeAveragesResultSchema>;

const ReviewCompletionResultSchema = z.object({
    revieweeCount: CountSchema,
    submittedCount: CountSchema,
    completionRate: z.number().min(0).max(100).nullable()
});
export type ReviewCompletionResult = z.infer<typeof ReviewCompletionResultSchema>;

const RevieweeSchema = z.object({
    memberId: z.string(),
    displayName: z.string(),
    jobTitle: z.string(),
    departmentLabel: z.string(),
    imageUrl: z.string().optional(),
    overallScore: z.number().nullable(),
    performanceScore: z.number().nullable(),
    potentialScore: z.number().nullable()
});
export type Reviewee = z.infer<typeof RevieweeSchema>;

const MaxScaleSchema = z.number().int().positive().optional();

const AxisScalesSchema = z.object({
    performanceMaxScale: MaxScaleSchema,
    potentialMaxScale: MaxScaleSchema
});

const RestrictToMemberIdsSchema = z.array(z.string()).optional();

const ListRevieweesInputSchema = z.object({
    ...PeriodSelectionSchema.shape,
    ...MemberFiltersSchema.shape,
    ...AxisScalesSchema.shape,
    restrictToMemberIds: RestrictToMemberIdsSchema,
    overallMaxScale: MaxScaleSchema,
    searchTerm: z.string().optional(),
    granularity: ScoreBucketGranularitySchema.default(DEFAULT_SCORE_BUCKET_GRANULARITY),
    scoreBand: z.number().positive().optional(),
    performanceBand: ScoreBandSchema.optional(),
    potentialBand: ScoreBandSchema.optional(),
    sortDir: z.enum(["asc", "desc"]).default("desc"),
    pageSize: z.number().int().positive().max(PRINT_PAGE_SIZE).default(25),
    continuationToken: z.string().optional()
});

export const REVIEWEES_EXPORT_PATH = "/api/analytics/review-scores/export";
export const CALIBRATION_SIGNALS_EXPORT_PATH = "/api/analytics/review-scores/export-calibration-signals";

export const ExportRevieweesInputSchema = ListRevieweesInputSchema.omit({ pageSize: true, continuationToken: true });
export type ExportRevieweesInput = z.infer<typeof ExportRevieweesInputSchema>;

const ListRevieweesResultSchema = z.object({
    items: z.array(RevieweeSchema),
    totalCount: CountSchema,
    continuationToken: z.string().optional()
});
export type ListRevieweesResult = z.infer<typeof ListRevieweesResultSchema>;

export const CalibrationDimensions = ["manager", "segment", "function", "jobTitle", "tenure"] as const;
const CalibrationDimensionSchema = z.enum(CalibrationDimensions);
export type CalibrationDimension = z.infer<typeof CalibrationDimensionSchema>;

export const UNSET_GROUP_KEY = "";

export function isUnsetGroupKey(groupKey: string): boolean {
    return !groupKey.trim();
}

export const CalibrationSortColumns = ["average", "spread", "gap"] as const;
const CalibrationSortColumnSchema = z.enum(CalibrationSortColumns);
export type CalibrationSortColumn = z.infer<typeof CalibrationSortColumnSchema>;

const CalibrationSignalSchema = z.object({
    groupKey: z.string(),
    groupLabel: z.string(),
    memberCount: CountSchema,
    averageScore: z.number().nullable(),
    spread: z.number().nullable(),
    gap: z.number().nullable(),
    canDrillDown: z.boolean()
});
export type CalibrationSignal = z.infer<typeof CalibrationSignalSchema>;

const ListCalibrationSignalsInputSchema = z.object({
    ...PeriodSelectionSchema.shape,
    ...MemberFiltersSchema.shape,
    restrictToGroupKeys: z.array(z.string()).optional(),
    dimension: CalibrationDimensionSchema.default("manager"),
    sortBy: CalibrationSortColumnSchema.default("gap"),
    sortDir: z.enum(["asc", "desc"]).default("desc"),
    pageSize: z.number().int().positive().max(PRINT_PAGE_SIZE).default(25),
    continuationToken: z.string().optional()
});

export const ExportCalibrationSignalsInputSchema = ListCalibrationSignalsInputSchema.omit({ pageSize: true, continuationToken: true });
export type ExportCalibrationSignalsInput = z.infer<typeof ExportCalibrationSignalsInputSchema>;

const ListCalibrationSignalsResultSchema = z.object({
    items: z.array(CalibrationSignalSchema),
    totalCount: CountSchema,
    continuationToken: z.string().optional()
});
export type ListCalibrationSignalsResult = z.infer<typeof ListCalibrationSignalsResultSchema>;

const CalibrationBaselinesResultSchema = z.object({
    orgAverage: z.number().nullable(),
    scale: z.number().int().positive()
});
export type CalibrationBaselinesResult = z.infer<typeof CalibrationBaselinesResultSchema>;

const CalibrationGroupInputSchema = z.object({
    ...PeriodSelectionSchema.shape,
    ...MemberFiltersSchema.shape,
    ...PeriodSelectionSchema.shape,
    dimension: CalibrationDimensionSchema,
    groupKey: z.string()
});

const ListCalibrationGroupMembersInputSchema = z.object({
    ...CalibrationGroupInputSchema.shape,
    sortDir: z.enum(["asc", "desc"]).default("desc"),
    pageSize: z.number().int().positive().default(10),
    continuationToken: z.string().optional()
});

const ListCalibrationGroupMembersResultSchema = z.object({
    items: z.array(RevieweeSchema),
    totalCount: CountSchema,
    continuationToken: z.string().optional()
});
export type ListCalibrationGroupMembersResult = z.infer<typeof ListCalibrationGroupMembersResultSchema>;

export const reviewScoresContract = {
    getReviewScoreDistribution: oc
        .route({ method: "GET", path: "/get-review-score-distribution" })
        .errors({
            UPSTREAM_FAILURE: {}
        })
        .input(ScoreDistributionInputSchema)
        .output(ReviewScoreDistributionResultSchema),

    getReviewScoreNineBox: oc
        .route({ method: "GET", path: "/get-review-score-nine-box" })
        .errors({
            UPSTREAM_FAILURE: {}
        })
        .input(z.object({ ...ScoreSelectionInputSchema.shape, ...AxisScalesSchema.shape }))
        .output(NineBoxResultSchema),

    getReviewCompletion: oc
        .route({ method: "GET", path: "/get-review-completion" })
        .errors({
            UPSTREAM_FAILURE: {}
        })
        .input(ScoreSelectionInputSchema)
        .output(ReviewCompletionResultSchema),

    listReviewerTypeAverages: oc
        .route({ method: "GET", path: "/list-reviewer-type-averages" })
        .errors({
            UPSTREAM_FAILURE: {}
        })
        .input(ScoreSelectionInputSchema)
        .output(ListReviewerTypeAveragesResultSchema),

    listReviewees: oc
        .route({ method: "GET", path: "/list-reviewees" })
        .errors({
            UPSTREAM_FAILURE: {}
        })
        .input(ListRevieweesInputSchema)
        .output(ListRevieweesResultSchema),

    listCalibrationSignals: oc
        .route({ method: "GET", path: "/list-calibration-signals" })
        .errors({
            UPSTREAM_FAILURE: {}
        })
        .input(ListCalibrationSignalsInputSchema)
        .output(ListCalibrationSignalsResultSchema),

    getCalibrationBaselines: oc
        .route({ method: "GET", path: "/get-calibration-baselines" })
        .errors({
            UPSTREAM_FAILURE: {}
        })
        .output(CalibrationBaselinesResultSchema),

    getCalibrationGroupSpread: oc
        .route({ method: "GET", path: "/get-calibration-group-spread" })
        .errors({
            UPSTREAM_FAILURE: {}
        })
        .input(CalibrationGroupInputSchema)
        .output(ScoreDistributionSchema),

    listCalibrationGroupMembers: oc
        .route({ method: "GET", path: "/list-calibration-group-members" })
        .errors({
            UPSTREAM_FAILURE: {}
        })
        .input(ListCalibrationGroupMembersInputSchema)
        .output(ListCalibrationGroupMembersResultSchema)
};
